import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import SectionBlock from "./SectionBlock";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { ChevronDown, ChevronRight, Folder, FolderOpen, MoreHorizontal, Pencil, CopyPlus, Eye, EyeOff, Trash2, ArrowUp, ArrowDown, Plus, X } from "lucide-react";

// A collapsible module (textbook chapter) card containing sections.
export default function ModuleCard({ module: mod, index, total, isInstructor, open, onToggle, isSectionOpen, onToggleSection, onChanged, onError, onMove, onGoToExams, onGoToProjects, materials, exams, projects, skillTags, tagFilter }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: mod.title, description: mod.description || "" });
  const [addingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState("");

  const act = async (fn) => {
    try { await fn(); onChanged(); }
    catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleHidden = () => act(() => api.patch(`/modules/${mod.id}`, { hidden: !mod.hidden }));
  const duplicate = () => act(() => api.post(`/modules/${mod.id}/duplicate`));
  const remove = () => {
    if (!window.confirm(`Delete module "${mod.title}" with all its sections and items? (Linked materials/exams/projects are NOT deleted.)`)) return;
    act(() => api.delete(`/modules/${mod.id}`));
  };
  const saveEdit = (e) => {
    e.preventDefault();
    act(() => api.patch(`/modules/${mod.id}`, { title: form.title, description: form.description }));
    setEditing(false);
  };
  const addSection = (e) => {
    e.preventDefault();
    if (!sectionName.trim()) return;
    act(() => api.post(`/modules/${mod.id}/sections`, { title: sectionName.trim() }));
    setSectionName("");
    setAddingSection(false);
  };

  return (
    <div data-testid={`module-${mod.id}`} className={`nb-card-static overflow-hidden ${mod.hidden ? "opacity-60" : ""}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 md:p-5">
        <button data-testid={`module-toggle-${mod.id}`} onClick={onToggle} className="flex items-center gap-3 min-w-0 flex-1 text-left group">
          {open ? <ChevronDown className="w-5 h-5 shrink-0 text-[var(--gold)]" /> : <ChevronRight className="w-5 h-5 shrink-0 text-[var(--gold)]" />}
          {open ? <FolderOpen className="w-6 h-6 shrink-0 text-[var(--gold)]" strokeWidth={1.5} /> : <Folder className="w-6 h-6 shrink-0 text-[var(--gold)]" strokeWidth={1.5} />}
          <div className="min-w-0">
            <h3 className="font-display text-lg md:text-xl font-bold truncate group-hover:text-[var(--gold)] transition-colors">
              {mod.title}
              {mod.hidden && isInstructor && <span data-testid={`module-hidden-badge-${mod.id}`} className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--muted-text)]/25 text-[var(--gold-soft)] align-middle">Hidden</span>}
            </h3>
            <p data-testid={`module-activity-count-${mod.id}`} className="text-xs text-[var(--muted-text)]">
              {mod.activity_count} {mod.activity_count === 1 ? "activity" : "activities"}
              {mod.description ? ` · ${mod.description}` : ""}
            </p>
          </div>
        </button>
        {isInstructor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`module-menu-${mod.id}`} className="nb-btn nb-btn-white text-sm px-2"><MoreHorizontal className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[75]">
              <DropdownMenuItem data-testid={`module-edit-${mod.id}`} onClick={() => { setForm({ title: mod.title, description: mod.description || "" }); setEditing(true); }}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
              <DropdownMenuItem data-testid={`module-duplicate-${mod.id}`} onClick={duplicate}><CopyPlus className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
              <DropdownMenuItem data-testid={`module-toggle-hidden-${mod.id}`} onClick={toggleHidden}>
                {mod.hidden ? <><Eye className="w-4 h-4 mr-2" /> Show</> : <><EyeOff className="w-4 h-4 mr-2" /> Hide</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={index === 0} onClick={() => onMove(index, -1)}><ArrowUp className="w-4 h-4 mr-2" /> Move up</DropdownMenuItem>
              <DropdownMenuItem disabled={index === total - 1} onClick={() => onMove(index, 1)}><ArrowDown className="w-4 h-4 mr-2" /> Move down</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`module-delete-${mod.id}`} onClick={remove} className="text-red-600 focus:text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Sections */}
      {open && (
        <div className="px-4 md:px-5 pb-4 space-y-3">
          {mod.sections.map((sec, si) => (
            <SectionBlock
              key={sec.id} section={sec} module={mod} sectionIndex={si}
              isInstructor={isInstructor}
              open={isSectionOpen(sec.id)} onToggle={() => onToggleSection(sec.id)}
              onChanged={onChanged} onError={onError}
              onGoToExams={onGoToExams} onGoToProjects={onGoToProjects}
              materials={materials} exams={exams} projects={projects} skillTags={skillTags} tagFilter={tagFilter}
            />
          ))}
          {isInstructor && (
            addingSection ? (
              <form onSubmit={addSection} className="flex gap-2">
                <input data-testid={`new-section-input-${mod.id}`} autoFocus value={sectionName} onChange={(e) => setSectionName(e.target.value)} className="nb-input text-sm" placeholder="Section title (e.g. Review It)" />
                <button data-testid={`new-section-save-${mod.id}`} className="nb-btn nb-btn-yellow text-sm">Add</button>
                <button type="button" onClick={() => setAddingSection(false)} className="nb-btn nb-btn-white text-sm"><X className="w-4 h-4" /></button>
              </form>
            ) : (
              <button data-testid={`add-section-btn-${mod.id}`} onClick={() => setAddingSection(true)} className="nb-btn nb-btn-white text-xs flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Add section
              </button>
            )
          )}
        </div>
      )}

      {/* Edit module dialog */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4" onClick={() => setEditing(false)}>
          <form data-testid={`module-edit-form-${mod.id}`} onSubmit={saveEdit} className="nb-card-static p-6 w-full max-w-md animate-pop space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold">Edit module</h3>
            <input data-testid={`module-edit-title-${mod.id}`} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nb-input" placeholder="Module title" />
            <input data-testid={`module-edit-desc-${mod.id}`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nb-input" placeholder="Short description (optional)" />
            <div className="flex gap-2">
              <button data-testid={`module-edit-save-${mod.id}`} className="nb-btn nb-btn-yellow flex-1">Save</button>
              <button type="button" onClick={() => setEditing(false)} className="nb-btn nb-btn-white">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
